import React, {ReactNode} from "react";
import ContentMode from "../ContentMode";
import Administration from "../session/Administration";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import FileHelper from "../../shared/helper/FileHelper";
import {AbstractPanel, AbstractPanelProperties, AbstractPanelState} from "./AbstractPanel";
import {
	JavaStatus,
	JavaStatusGC,
	JavaStatusOsDistro,
	ServerStatus,
	ServerVersionInfo
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Translate from "../../shared/translation/Translate";
import Helper from "../../shared/components/Helper";
import DateHelper from "../../shared/helper/DateHelper";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import ProgressCircle from "../../shared/components/ProgressCircle";
import NumberHelper from "../../shared/helper/NumberHelper";
import {Dropdown} from "primereact/dropdown";
import Timer from "../../shared/class/Timer";

export interface InfoPanelProperties extends AbstractPanelProperties {
}

export interface InfoPanelState extends AbstractPanelState {
}

export class InfoPanelDefinition extends AbstractPanel<InfoPanelProperties, InfoPanelState> {
	private updateTimer: Timer;

	constructor(props: Readonly<InfoPanelProperties>) {
		super(props);

		this.state = {
			...super.state
		};

		this.updateTimer = new Timer(async () => {
			if (!FunctionHelper.exists(this.props.context.user)) {
				return;
			}

			let administration: Administration = this.props.context.session.getAdministration() as Administration;

			try {
				await administration.updateServerStatus();
				this.setState({
					exception: undefined
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await administration.updateServerStatus();
				}
			}
		}, 5000, true);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		let administration: Administration = this.props.context.session.getAdministration() as Administration;

		if (FunctionHelper.exists(this.props.context.user)) {
			try {
				await administration.updateServerStatus();
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await administration.updateServerStatus();
				}
			}
		}

		this.updateTimer.start();
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();
		this.updateTimer.pause();
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<InfoPanelProperties>, prevState: Readonly<InfoPanelState>, snapshot?: any
	): Promise<void> {
		if (this.props.contentMode !== prevProps.contentMode) {
			this.updateTimer.pause();

			if (this.props.contentMode === ContentMode.INFO) {
				this.updateTimer.start();
			}
		}
	}

	private renderOptions(): ReactNode {
		return (
			<div className="functions">
				<div className="input-group">
					<label htmlFor="control-update">
						<Translate {...this.props} data="adminscreen.panels.info.update" namespace="screen"/>
					</label>
					<Dropdown value={this.updateTimer.getTime()} inputId="control-update"
							  onChange={(e) => {
								  this.updateTimer.setTime(e.value);
								  this.updateTimer.reset();
								  this.forceUpdate();
							  }}
							  appendTo={document.getElementById("root")}
							  options={[
								  {
									  label: DateHelper.formatTime(1000),
									  value: 1000
								  },
								  {
									  label: DateHelper.formatTime(5000),
									  value: 5000
								  },
								  {
									  label: DateHelper.formatTime(10000),
									  value: 10000
								  }
							  ]}/>
				</div>
			</div>
		);
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let serverStatus: ServerStatus | undefined = ServerStatus.fromJson(this.props.serverStatus?.get());
		if (!FunctionHelper.exists(serverStatus) || !FunctionHelper.exists(this.props.context.user)) {
			return null;
		}

		let serverStart: Date = new Date(FunctionHelper.applyValue(serverStatus.server?.serverStart, 0));
		let currentTime: Date = new Date();
		let diff: number = currentTime.getTime() - serverStart.getTime();
		let days: number = Math.floor(diff / (1000 * 3600 * 24));
		diff %= 1000 * 3600 * 24;
		let hours: number = Math.floor(diff / (1000 * 3600));
		diff %= 1000 * 3600;
		let minutes: number = Math.floor(diff / (1000 * 60));

		let totalMemory: number = parseInt(FunctionHelper.applyValue(
			serverStatus?.java?.memoryMax, JavaStatus.getMemoryMaxDefault()
		), 10) * 1024 * 1024;
		let totalFreeMemory: number = parseInt(FunctionHelper.applyValue(
			serverStatus?.java?.memoryTotalFree, JavaStatus.getMemoryTotalFreeDefault()
		), 10) * 1024 * 1024;
		let allocatedMemory: number = parseInt(FunctionHelper.applyValue(
			serverStatus?.java?.memoryAllocated, JavaStatus.getMemoryAllocatedDefault()
		), 10) * 1024 * 1024;
		let usedMemory: number = parseInt(FunctionHelper.applyValue(
			serverStatus?.java?.memoryUsed, JavaStatus.getMemoryUsedDefault()
		), 10) * 1024 * 1024;

		let osName: string = FunctionHelper.applyValue(
			serverStatus?.java?.osName, JavaStatus.getOsNameDefault()
		);

		let gcItems: Array<ReactNode> = [];
		for (let [index, gcItem] of FunctionHelper.applyValue(
			serverStatus.java?.gc, JavaStatus.getGcDefault()
		).entries()) {
			let time: number = FunctionHelper.applyValue(gcItem.collectionTime, JavaStatusGC.getCollectionTimeDefault());
			let count: number = FunctionHelper.applyValue(gcItem.collectionCount, JavaStatusGC.getCollectionCountDefault());

			gcItems.push(
				<tr key={index}>
					<td>{FunctionHelper.applyValue(gcItem.name, JavaStatusGC.getNameDefault())}</td>
					<td>{FunctionHelper.applyStringValue(DateHelper.formatTime(time, true), "0")}</td>
					<td>{FunctionHelper.applyStringValue(count, "0")}</td>
					<td>{FunctionHelper.applyStringValue(
						DateHelper.formatTime(count / time * 1000, true), "0"
					)}</td>
				</tr>
			);
		}

		let values: Array<{ label: any, value: any }> = [
			{
				label: <Translate {...this.props} data="adminscreen.panels.info.start.label" namespace="screen"/>,
				value: <>
					{DateHelper.buildTimestampString(serverStart, this.props.language)} (
					<Translate {...this.props} data="adminscreen.panels.info.start.day" namespace="screen"
							   values={{count: days}}/>
					<Translate {...this.props} data="adminscreen.panels.info.start.hour" namespace="screen"
							   values={{count: hours}}/>
					<Translate {...this.props} data="adminscreen.panels.info.start.minute" namespace="screen"
							   values={{count: minutes}}/>)
				</>
			},
			{
				label: <Translate {...this.props} data="adminscreen.panels.info.memory.max" namespace="screen"/>,
				value: FileHelper.formatFileSize(totalMemory, 2)
			},
			{
				label: <Translate {...this.props} data="adminscreen.panels.info.memory.total_free" namespace="screen"/>,
				value: FileHelper.formatFileSize(totalFreeMemory, 2)
			},
			{
				label: <Translate {...this.props} data="adminscreen.panels.info.memory.allocated" namespace="screen"/>,
				value: FileHelper.formatFileSize(allocatedMemory, 2)
			},
			{
				label: <Translate {...this.props} data="adminscreen.panels.info.memory.used" namespace="screen"/>,
				value: FileHelper.formatFileSize(usedMemory, 2)
			},
			{
				label: <Translate {...this.props} data="adminscreen.panels.info.version" namespace="screen"/>,
				value: FunctionHelper.applyValue(
					serverStatus?.server?.serverVersion, ServerVersionInfo.getServerVersionDefault()
				)
			},
			{
				label: <Translate {...this.props} data="adminscreen.panels.info.OS" namespace="screen"/>,
				value: FunctionHelper.applyValue(
					serverStatus?.java?.osName, JavaStatus.getOsNameDefault()
				)
			}
		];

		let osDistroName: string | undefined;
		if (osName.toLowerCase().includes("linux")) {
			osDistroName = FunctionHelper.applyValue(
				serverStatus.java?.osDistro?.name, JavaStatusOsDistro.getNameDefault()
			);

			let osVersion: string | undefined = serverStatus.java?.osDistro?.version;
			if (FunctionHelper.exists(osVersion)) {
				osDistroName += " " + osVersion;
			}
		}

		if (FunctionHelper.exists(osDistroName)) {
			values.push({
				label: <Translate {...this.props} data="adminscreen.panels.info.OSDistro" namespace="screen"/>,
				value: osDistroName
			});
		}

		let containerName: string | undefined = serverStatus.java?.container?.name;
		if (FunctionHelper.exists(containerName)) {
			values.push({
				label: <Translate {...this.props} data="adminscreen.panels.info.container" namespace="screen"/>,
				value: containerName
			});
		}

		values = values.concat([
			{
				label: <Translate {...this.props} data="adminscreen.panels.info.jvm.name" namespace="screen"/>,
				value: FunctionHelper.applyValue(
					serverStatus?.java?.javaName, JavaStatus.getJavaNameDefault()
				)
			},
			{
				label: <Translate {...this.props} data="adminscreen.panels.info.jvm.architecture" namespace="screen"/>,
				value: FunctionHelper.applyValue(
					serverStatus?.java?.osArchitecture, JavaStatus.getOsArchitectureDefault()
				)
			},
			{
				label: <Translate {...this.props} data="adminscreen.panels.info.jvm.version" namespace="screen"/>,
				value: FunctionHelper.applyValue(
					serverStatus?.java?.javaVersion, JavaStatus.getJavaVersionDefault()
				)
			},
			{
				label: <Translate {...this.props} data="adminscreen.panels.info.jvm.vendor" namespace="screen"/>,
				value: FunctionHelper.applyValue(
					serverStatus?.java?.javaVendor, JavaStatus.getJavaVendorDefault()
				)
			}
		]);

		let usedPercentage: number = NumberHelper.format(usedMemory / totalMemory * 100, 2);
		let allocatedPercentage: number = NumberHelper.format((allocatedMemory - usedMemory) / totalMemory * 100, 2);
		let freePercentage: number = NumberHelper.format((totalMemory - allocatedMemory) / totalMemory * 100, 2);

		return (
			<div className="screen admin-info-panel" data-help="global.screen.admin.content" data-position="top">
				<div className="body">
					<h1>
						<Translate {...this.props} data={"adminscreen.panels.info.title"} namespace="screen"/>
						{this.renderHelpButton()}
					</h1>
					<Helper {...this.props}>
						<Translate {...this.props} data={"global.screen.admin.info"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<fieldset className="p-col-12 p-grid">
						{this.renderError()}
					</fieldset>
					{this.renderOptions()}
					<div className="p-grid">
						<fieldset className="p-col-12 p-grid">
							<div className="p-col-12 p-xl-6">
								<DataTable value={values} responsiveLayout={"scroll"}>
									<Column field="label"/>
									<Column field="value"/>
								</DataTable>
							</div>
							<div className="p-col-12 p-xl-6 p-grid" style={{justifyContent: "center"}}>
								<ProgressCircle startAngle={-90} endAngle={90} enableLabels={true} arcLabel={"label"}
												labelThreshold={1}
												data={[
													{
														id: "used",
														label: usedPercentage + "% | " +
															FileHelper.formatFileSize(usedMemory, 2),
														value: usedPercentage,
														color: "#fbc200"
													},
													{
														id: "allocated",
														label: allocatedPercentage + "% | " +
															FileHelper.formatFileSize(allocatedMemory, 2),
														value: allocatedPercentage, color: "#fff3c8"
													},
													{
														id: "empty",
														label: freePercentage + "% | " +
															FileHelper.formatFileSize(totalFreeMemory, 2),
														value: freePercentage, color: "#ffffff"
													}
												]}
												layers={[
													(options: { centerX: number, centerY: number }) => {
														return (
															<>
																<text x={options.centerX} y={options.centerY - 10}
																	  textAnchor="middle" dominantBaseline="central"
																	  className="center-label"
																	  style={{
																		  fontSize: '18px'
																	  }}>
																	<tspan x={options.centerX} y={options.centerY - 70}>
																		<Translate {...this.props} namespace={"screen"}
																				   data={"adminscreen.panels.info.memory.label"}/>
																	</tspan>
																	<tspan x={options.centerX} y={options.centerY - 30}>
																		{FileHelper.formatFileSize(usedMemory, 2)}/
																		{FileHelper.formatFileSize(totalMemory, 2)}
																	</tspan>
																</text>
																<text textAnchor="middle" dominantBaseline="central"
																	  style={{
																		  fontSize: '14px'
																	  }}>
																	<tspan x={50} y={270}>
																		{FileHelper.formatFileSize(0, 2)}
																	</tspan>
																	<tspan x={400} y={270}>
																		{FileHelper.formatFileSize(totalMemory, 2)}
																	</tspan>

																</text>
															</>
														)
													}
												]}/>

								{gcItems.length > 0 &&
									<table className="gc-table">
										<thead>
										<tr>
											<th>
												<Translate {...this.props} namespace={"screen"}
														   data={"adminscreen.panels.info.gc.name"}/>
											</th>
											<th>
												<Translate {...this.props} namespace={"screen"}
														   data={"adminscreen.panels.info.gc.time"}/>
											</th>
											<th>
												<Translate {...this.props} namespace={"screen"}
														   data={"adminscreen.panels.info.gc.count"}/>
											</th>
											<th>
												<Translate {...this.props} namespace={"screen"}
														   data={"adminscreen.panels.info.gc.average"}/>
											</th>
										</tr>
										</thead>
										<tbody>{gcItems}</tbody>
									</table>
								}
							</div>
							<div className="p-col-12">
								<h2>
									<Translate {...this.props} data={"adminscreen.panels.info.license"}
											   namespace="screen"/>
								</h2>
								<pre>{
									FunctionHelper.applyValue(
										serverStatus?.server?.licenseText, ServerVersionInfo.getLicenseTextDefault()
									)
								}</pre>
							</div>
						</fieldset>
					</div>
				</div>
			</div>
		);
	}
}

const InfoPanel = React.memo(InfoPanelDefinition,
	(prevProps: Readonly<InfoPanelProperties>, nextProps: Readonly<InfoPanelProperties>) => {
		if (nextProps.contentMode !== prevProps.contentMode) {
			return false;
		}

		if (prevProps.language !== nextProps.language) {
			return false;
		}

		if (prevProps.serverStatus !== nextProps.serverStatus) {
			return false;
		}

		if (prevProps.showHelp !== nextProps.showHelp) {
			return false;
		}

		return true;
	}
);

export default InfoPanel;